import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Crown, ExternalLink, Shield, Trash2, Users, Waypoints } from 'lucide-react';
import { api } from '@/services/api';
import { Card, CardHeader } from '@/components/ui/card';
import { DataTable } from '@/components/tables/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Drawer } from '@/components/ui/drawer';
import { EmptyState } from '@/components/ui/empty-state';
import { useToast } from '@/components/ui/toast';
import { PageHeader } from '@/components/ui/page-header';
import { ActionMenu } from '@/components/ui/action-menu';
import { useConfirmation } from '@/components/ui/confirmation';

export function ClubsPage() {
  const [selected, setSelected] = useState();
  const client = useQueryClient();
  const toast = useToast();
  const confirmAction = useConfirmation((state) => state.confirm);
  const groups = useQuery({
    queryKey: ['admin-clubs'],
    queryFn: () => api('/api/v1/admin/groups').then((result) => result.data),
  });
  const members = useQuery({
    queryKey: ['admin-club-members', selected?._id],
    queryFn: () => api(`/api/v1/admin/groups/${selected._id}/members`).then((result) => result.data),
    enabled: Boolean(selected),
  });
  const rows = groups.data ?? [];
  const summary = useMemo(() => ({
    clubs: rows.length,
    riders: rows.reduce((total, item) => total + Number(item.memberCount ?? 0), 0),
    active: rows.reduce((total, item) => total + Number(item.activeConvoyCount ?? 0), 0),
  }), [rows]);

  const refreshMembers = () => client.invalidateQueries({ queryKey: ['admin-club-members', selected?._id] });

  const makeOwner = async (memberId) => {
    if (!await confirmAction({ title: 'Transfer ownership?', message: 'The selected rider will become the club owner.', confirmLabel: 'Transfer' })) return;
    try {
      await api(`/api/v1/admin/groups/${selected._id}/owner`, {
        method: 'PATCH',
        body: JSON.stringify({ memberId }),
      });
      await Promise.all([
        refreshMembers(),
        client.invalidateQueries({ queryKey: ['admin-clubs'] }),
      ]);
      toast.show('Ownership transferred', 'success');
    } catch (error) {
      toast.show(error.message, 'error', 'Transfer failed');
    }
  };

  const remove = async (memberId) => {
    if (!await confirmAction({ title: 'Remove rider?', message: 'The rider will lose access to this club.', confirmLabel: 'Remove', tone: 'danger' })) return;
    try {
      await api(`/api/v1/admin/groups/${selected._id}/members/${memberId}`, { method: 'DELETE' });
      await refreshMembers();
      toast.show('Rider removed', 'success');
    } catch (error) {
      toast.show(error.message, 'error', 'Remove failed');
    }
  };

  const removeSelected = async (selectedRows, clear) => {
    const removable = selectedRows.filter((item) => item.position !== 'owner');
    if (!removable.length || !await confirmAction({ title: `Remove ${removable.length} riders?`, message: 'Selected riders will lose access to this club.', confirmLabel: 'Remove riders', tone: 'danger' })) return;
    try {
      await Promise.all(removable.map((item) => api(`/api/v1/admin/groups/${selected._id}/members/${item.memberId}`, { method: 'DELETE' })));
      clear();
      await refreshMembers();
      toast.show(`${removable.length} rider(s) removed`, 'success');
    } catch (error) {
      toast.show(error.message, 'error', 'Bulk remove failed');
    }
  };

  const columns = [
    {
      key: 'name',
      header: 'Club',
      sortValue: (row) => row.name,
      cell: (row) => <div className="max-w-sm"><div className="flex items-center gap-2"><span className="grid size-8 place-items-center border border-cyan-300/20 bg-cyan-300/[.05] text-cyan-300"><Users size={14}/></span><div><p className="font-semibold text-slate-100">{row.name}</p><p className="max-w-64 truncate text-[10px] text-slate-600">{row.description || 'Independent rider club'}</p></div></div></div>,
    },
    {
      key: 'owner',
      header: 'Owner',
      sortValue: (row) => row.owner?.name ?? '',
      cell: (row) => <div><p className="text-xs font-semibold text-slate-300">{row.owner?.name ?? 'Unknown owner'}</p><p className="text-[10px] text-slate-600">{row.owner?.email ?? String(row.ownerId)}</p></div>,
    },
    {
      key: 'members',
      header: 'Riders',
      sortValue: (row) => row.memberCount ?? 0,
      cell: (row) => <div><p className="font-mono text-sm font-black text-white">{row.memberCount ?? 0}</p><p className="text-[9px] uppercase tracking-[.12em] text-slate-700">{row.moderatorCount ?? 0} managers</p></div>,
    },
    {
      key: 'convoys',
      header: 'Active convoy',
      sortValue: (row) => row.activeConvoyCount ?? 0,
      cell: (row) => <Badge tone={row.activeConvoyCount ? 'success' : 'neutral'}>{row.activeConvoyCount ?? 0} active</Badge>,
    },
    {
      key: 'policy',
      header: 'Invitations',
      sortValue: (row) => row.invitationPolicy,
      cell: (row) => <span className="text-xs capitalize text-slate-400">{String(row.invitationPolicy).replaceAll('_', ' ')}</span>,
    },
    {
      key: 'updated',
      header: 'Updated',
      sortValue: (row) => row.updatedAt,
      cell: (row) => <span className="text-xs text-slate-500">{new Date(row.updatedAt).toLocaleString()}</span>,
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      exportable: false,
      hideable: false,
      cell: (row) => <Button variant="secondary" className="h-8 px-3 text-xs" onClick={(event) => { event.stopPropagation(); setSelected(row); }}><ExternalLink size={12}/>Open</Button>,
    },
  ];

  const memberColumns = [
    {
      key: 'member',
      header: 'Rider',
      sortValue: (row) => row.member?.name ?? '',
      cell: (row) => <div><p className="text-xs font-semibold text-slate-200">{row.member?.name ?? 'Unknown rider'}</p><p className="text-[10px] text-slate-600">{row.member?.email ?? row.memberId}</p></div>,
    },
    {
      key: 'position',
      header: 'Club role',
      sortValue: (row) => row.position,
      cell: (row) => <Badge tone={row.position === 'owner' ? 'accent' : row.position === 'moderator' ? 'success' : 'neutral'}>{row.position}</Badge>,
    },
    {
      key: 'status',
      header: 'Account',
      sortValue: (row) => row.member?.status ?? '',
      cell: (row) => <Badge tone={row.member?.status === 'active' ? 'success' : 'warning'}>{row.member?.status ?? 'unknown'}</Badge>,
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      exportable: false,
      cell: (row) => row.position === 'owner' ? null : <ActionMenu actions={[{ label: 'Transfer ownership', icon: Crown, onClick: () => void makeOwner(row.memberId) }, { label: 'Remove rider', icon: Trash2, danger: true, onClick: () => void remove(row.memberId) }]}/>,
    },
  ];

  return <>
    <div className="workspace-page">
      <PageHeader eyebrow="Network" title="Clubs" meta={<Badge tone="accent">{summary.clubs} clubs</Badge>}/>
      <div className="grid gap-3 sm:grid-cols-3"><SummaryCard label="Clubs" value={summary.clubs} icon={Users}/><SummaryCard label="Registered riders" value={summary.riders} icon={Shield}/><SummaryCard label="Active convoys" value={summary.active} icon={Waypoints}/></div>
      <Card className="workspace-fill">
        <CardHeader eyebrow="Independent communities" title="Club directory"/>
        {rows.length ? <DataTable
          tableId="clubs"
          rows={rows}
          columns={columns}
          getKey={(row) => row._id}
          onRowClick={setSelected}
          onRefresh={() => groups.refetch()}
          loading={groups.isFetching}
          searchPlaceholder="Search club, owner, or policy"
        /> : <EmptyState title={groups.isLoading ? 'Loading clubs' : 'No clubs'}/>} 
      </Card>
    </div>
    <Drawer open={Boolean(selected)} title={selected?.name ?? 'Club'} eyebrow="Club control" onClose={() => setSelected(undefined)} width="max-w-4xl">
      {selected && <div className="space-y-5">
        <div className="grid gap-2 sm:grid-cols-4"><Info label="Owner" value={selected.owner?.name ?? 'Unknown'}/><Info label="Riders" value={selected.memberCount ?? 0}/><Info label="Managers" value={selected.moderatorCount ?? 0}/><Info label="Active convoy" value={selected.activeConvoyCount ?? 0}/></div>
        <div className="border border-white/8 bg-black/10"><div className="border-b border-white/8 px-4 py-3"><p className="text-xs font-bold text-slate-200">Members</p><p className="text-[10px] text-slate-600">Ownership stays inside this club.</p></div>{members.data?.length ? <DataTable
          tableId={`club-members-${selected._id}`}
          rows={members.data}
          columns={memberColumns}
          getKey={(row) => row._id}
          onRefresh={() => members.refetch()}
          loading={members.isFetching}
          bulkActions={[{ label: 'Remove', variant: 'danger', icon: <Trash2 size={12}/>, onClick: removeSelected }]}
        /> : <EmptyState title={members.isLoading ? 'Loading riders' : 'No riders'}/>}</div>
      </div>}
    </Drawer>
  </>;
}

export const GroupsPage = ClubsPage;

function SummaryCard({ label, value, icon: Icon }) {
  return <div className="flex items-center gap-3 border border-white/8 bg-[#0a1017]/90 p-4"><span className="grid size-10 place-items-center border border-cyan-300/20 bg-cyan-300/[.045] text-cyan-300"><Icon size={16}/></span><div><p className="font-mono text-xl font-black text-white">{value}</p><p className="text-[9px] font-black uppercase tracking-[.15em] text-slate-600">{label}</p></div></div>;
}

function Info({ label, value }) {
  return <div className="border border-white/8 bg-white/[.02] p-3"><p className="text-[9px] font-black uppercase tracking-[.14em] text-slate-700">{label}</p><p className="mt-1 truncate text-sm font-bold text-slate-200">{value}</p></div>;
}
